import { orderedWorkflowVersions } from "./workflowVersions";
import React, { useEffect, useRef, useState } from "react";
import { enqueueSnackbar } from "notistack";
import {
  Alert,
  Button,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import ConfirmationDialog from "./ConfirmationDialog";

export default function VersionContinuation({
  workflow,
  source,
  api,
  onChanged,
}) {
  const [destination, setDestination] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const request = useRef(0);
  useEffect(
    () => () => {
      request.current += 1;
    },
    [],
  );
  const targets = orderedWorkflowVersions(workflow).filter(
    (v) => v.status === "LIVE" && v.id !== source.id,
  );
  const inspect = async (offset = 0) => {
    const token = ++request.current;
    setBusy(true);
    setError("");
    try {
      const result = await api.getContinuationPreview(
        source.id,
        destination,
        offset,
      );
      if (token === request.current) setPreview(result);
    } catch (e) {
      if (token === request.current) setError(e.message);
    } finally {
      if (token === request.current) setBusy(false);
    }
  };
  const migrate = async () => {
    setConfirm(false);
    setBusy(true);
    setError("");
    const token = ++request.current;
    let cursor = progress?.afterId || 0;
    let migrated = progress?.migrated || 0;
    let excluded = progress?.excluded || 0;
    try {
      let more = true;
      while (more && token === request.current) {
        const result = await api.continueParticipants(
          source.id,
          destination,
          cursor,
        );
        migrated += result.results.filter(
          (item) => item.outcome === "migrated",
        ).length;
        excluded += result.results.filter(
          (item) => item.outcome !== "migrated",
        ).length;
        cursor = result.after_id;
        more = result.has_more;
        if (token === request.current)
          setProgress({ afterId: cursor, migrated, excluded, complete: !more });
      }
      if (token === request.current) {
        enqueueSnackbar(`Migrated ${migrated}; excluded ${excluded}.`, {
          variant: "success",
        });
        await onChanged();
      }
    } catch (e) {
      if (token === request.current) {
        setError(
          `${e.message} Retry to continue from the last completed batch.`,
        );
        enqueueSnackbar(e.message, { variant: "error" });
      }
    } finally {
      if (token === request.current) setBusy(false);
    }
  };
  if (source.status === "ARCHIVED" || !targets.length) return null;
  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h6">
          Continue participants on another version
        </Typography>
        <Typography color="text.secondary">
          Move active participants from version {source.versionNumber},
          preserving their stage, pending email and delivery history. Previously
          sent clones are skipped. Incompatible participants stay here.
        </Typography>
        <TextField
          select
          label="Destination version"
          value={destination}
          disabled={busy}
          onChange={(e) => {
            request.current += 1;
            setDestination(e.target.value);
            setPreview(null);
            setProgress(null);
            setError("");
          }}
        >
          {targets.map((v) => (
            <MenuItem key={v.id} value={v.id}>
              Version {v.versionNumber}
              {v.id === workflow.defaultVersion?.id ? " · Default" : ""}
            </MenuItem>
          ))}
        </TextField>
        <Button disabled={!destination || busy} onClick={() => inspect()}>
          Preview continuation
        </Button>
        {error && <Alert severity="error">{error}</Alert>}
        {preview && (
          <>
            <Typography>
              {preview.total} participants considered;{" "}
              {preview.counts.eligible || 0} eligible.{" "}
              {Object.entries(preview.counts)
                .filter(([key]) => key !== "eligible")
                .map(([key, count]) => `${count} ${key.replaceAll("_", " ")}`)
                .join("; ")}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    "Participant",
                    "Result / stage",
                    "Pending email",
                    "Catch-up first",
                    "Pending due",
                  ].map((label) => (
                    <TableCell key={label}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.details.map((item) => (
                  <TableRow key={item.source_enrollment_id}>
                    <TableCell>{item.recipient}</TableCell>
                    <TableCell>
                      {item.outcome === "eligible"
                        ? item.stage_name
                        : item.outcome.replaceAll("_", " ")}
                    </TableCell>
                    <TableCell>
                      {item.anchor_name
                        ? `${item.anchor_name}: ${item.source_position} → ${item.destination_position}`
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {item.catch_up_names?.join(", ") || "None"}
                    </TableCell>
                    <TableCell>
                      {item.due_at
                        ? new Date(item.due_at).toLocaleString()
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Stack direction="row">
              <Button
                disabled={busy || !preview.offset}
                onClick={() => inspect(Math.max(0, preview.offset - 50))}
              >
                Previous
              </Button>
              <Button
                disabled={busy || !preview.has_more}
                onClick={() => inspect(preview.offset + 50)}
              >
                Next
              </Button>
            </Stack>
            <Button
              disabled={busy || !preview.counts.eligible || progress?.complete}
              onClick={() => setConfirm(true)}
            >
              {progress && !progress.complete
                ? "Retry continuation"
                : "Migrate eligible participants"}
            </Button>
          </>
        )}
        {progress && (
          <Typography role="status">
            {progress.migrated} migrated; {progress.excluded} excluded.{" "}
            {progress.complete
              ? "Complete."
              : "Progress is saved after each batch."}
          </Typography>
        )}
        <ConfirmationDialog
          open={confirm}
          title="Migrate eligible participants?"
          message={`Continue on version ${targets.find((v) => v.id === destination)?.versionNumber}. Catch-up emails run before the saved pending email. Eligibility and position are checked again; some participants may be excluded. Normal delivery windows still apply.`}
          confirmLabel="Migrate"
          onCancel={() => setConfirm(false)}
          onConfirm={migrate}
        />
      </Stack>
    </Paper>
  );
}
